import {ApplicationRef, inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of, Subject, Subscription, timer} from "rxjs";
import {switchMap, tap, delay, take, map, catchError} from "rxjs/operators";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import { LoggerService } from "../../services/logger.service"

@Injectable({
  providedIn: 'root'
})
export class SessionService {

  private static readonly WARNING_TIMEOUT = 780000; // 780000 = 13 minutes
  private static readonly ERROR_TIMEOUT   = 120000; // 120000 = 2 minutes

  public static readonly ADV_NUMBER_HEADER_KEY = 'AAdvantage-Number';

  protected sessionCycle?: Subscription;
  private headers: HttpHeaders = new HttpHeaders();
  private advNumber: string | undefined;
  private loyaltyPoints: string | undefined; // is this needed?

  private appRef = inject(ApplicationRef);
  private httpClient = inject(HttpClient);
  private logger = inject(LoggerService);

  // Boolean observable to display or not display dialog
  private dialogOpen$ = new BehaviorSubject<boolean>(false);
  // what type of dialog is it (warning / expired)
  private dialogExpired$ = new BehaviorSubject<boolean>(false);

  dialogResult$?: Subject<boolean>;

  /**
   * Opens session dialog by emitting true to dialogOpen
   * @param sessionExpired boolean to determine is it a warning or expiration dialog
   */
  openSessionDialog(sessionExpired){
    this.dialogOpen$.next(true);
    this.dialogExpired$.next(sessionExpired);

    this.dialogResult$ = new Subject<boolean>();
    return this.dialogResult$.asObservable().pipe(
      take(1),
      tap(() => {
        this.dialogOpen$.next(false);
        this.dialogResult$ = undefined;
      })
    );
  }

  /**
   * Allows dialog to subscribe to the open variable
   */
  getDialogOpenStream(){
    return this.dialogOpen$.asObservable();
  }

  /**
   * Allows dialog to subscribe to the "is expired" variable
   */
  getDialogExpiredStream(){
    return this.dialogExpired$.asObservable();
  }

  /**
   * Initialization function that starts timer.
   */
  startSession(){
    this.logger.debug("Session Timer has been started");
    /*
    Why do we call subscribe here? Because observables don't do anything
    until you subscribe. RxJS Observables are lazy.
    * */
    this.sessionCycle = this.restartTimeout().subscribe();
  }

  /**
   * Return HttpHeaders to use in Network requests.
   * If session is active, calls loyalty and restarts session timer.
   * @returns Observable returning HttpHeaders
   */
  refreshSession(){
    if(this.sessionCycle === undefined || this.sessionCycle.closed){
      return;
    }

    this.logger.debug("Session Timer has been refreshed");
    this.sessionCycle?.unsubscribe();

    this.sessionCycle = this.restartTimeout().subscribe();

    return this.callLoyalty().pipe(
      map((advNumber) => {
        if(advNumber) {
          // Logged in User
          this.advNumber = advNumber;
          this.headers = this.headers.set(SessionService.ADV_NUMBER_HEADER_KEY, advNumber);
        } // Guest session, or non digital clients
        return this.headers;
      })
    );
  }

  /**
   * Full async timeout process deploying
   *  private method to activate session timeout dialog when timer expires.
   * Will call openSessionDialog and subscribe to the result to
   *  either restart timer or go to expired dialog
   */
  private restartTimeout() {
    return timer(SessionService.WARNING_TIMEOUT).pipe(
      tap(() => {
        this.logger.debug("Session warning timer reached");
        this.openSessionDialog(false).subscribe({
          next: (result) => {
            this.refreshSession().subscribe();
          },
          error: (error) => { }
        });
      }),
      delay(SessionService.ERROR_TIMEOUT),
      switchMap(() => {
        this.logger.debug("Session expiration timer reached");
        this.clearSessionStorage();
        return this.openSessionDialog(true);
      }),
      tap(() => {
        this.logger.debug("Session expired. Redirecting to homepage.");
        // TODO: update this?
        window.location.href = "/homePage.do";
      })
    );
  }


  /**
   * Call Loyalty to refresh session and get up-to-date token
   * @returns Observable promising advNumber or undefined
   */
  private callLoyalty(): Observable<string | undefined> {
    if(window.location.hostname === "localhost"){
      return of(undefined);
    }
    return this.httpClient.get<IAuthStatusResponse>('/loyalty/access-level').pipe(
      map((response) => response.status),
      catchError(error => {
        this.logger.error("Failed to refresh monolith session");
        return of(AuthStatus.GUEST);
      }),
      switchMap((status) => {
        if(status == AuthStatus.SECURED)
          return this.httpClient.get<IAuthUserResponse>('/loyalty/api/member-information');
        else
          return of(undefined);
      }),
      catchError(error => {
        this.logger.error("Failed to get advNumber from loyalty");
        return of(undefined);
      }),
      map((response: IAuthUserResponse | undefined) => {
        if(response){
          this.loyaltyPoints = response.loyaltyPointsFormatted;
          return response.aadvantageNumber;
        }
        else
          return undefined;
      })
    );
  }


  private clearSessionStorage(){
    sessionStorage.setItem('recordLocator', '');
    sessionStorage.setItem('lastName', '');
  }

  /**
   * Cancels session timer by unsubscribing
   */
  clearTimer() {
    this.logger.debug("Stopping session timer.");
    this.sessionCycle?.unsubscribe();
  }
}


enum AuthStatus {
  GUEST = "0",
  UNSECURED = "1",
  SECURED = "2"
}

interface IAuthUserResponse {
  aadvantageNumber: string;
  loyaltyPointsFormatted: string;
}

interface IAuthStatusResponse {
  status: AuthStatus;
}

export const ALLOWED_HOSTS = ['localhost','viewres-dev-testing.qa.aa.com','viewres-temp-testing.qa.aa.com', 'iqa2.qa.aa.com'];
